// Client-side export of the verified comparison result (inspector + accepted/edited
// AI detections). This is a SEPARATE export from the raw OCR exports — those are
// left untouched. Frontend-only (no backend) for CSV.

export interface VerifiedRow {
  main_Code?: string;
  distance?: number;
  timestamp?: string;
  c1?: string;
  c2?: string;
  sa?: string;
  q1?: string;
  q2?: string;
  postionFrom?: string;
  positionTo?: string;
  text?: string;
}

const csvEscape = (v: unknown): string => {
  const s = String(v ?? "");
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

// ISO timestamp -> HH:MM:SS (matches the on-screen table); falls back to raw value.
const timeLabel = (ts?: string): string => {
  if (!ts) return "";
  const d = new Date(ts);
  return Number.isNaN(d.getTime())
    ? ts
    : d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
};

const HEADERS = [
  "Time",
  "Distance (m)",
  "Main code",
  "C1",
  "C2",
  "SA",
  "Q1",
  "Q2",
  "Position from",
  "Position to",
  "Text",
];

export function toVerifiedCsv(rows: VerifiedRow[]): string {
  const lines = [HEADERS.join(",")];
  rows.forEach((r) => {
    lines.push(
      [
        timeLabel(r.timestamp),
        Number(r.distance ?? 0).toFixed(2),
        r.main_Code ?? "",
        r.c1 ?? "",
        r.c2 ?? "",
        r.sa ?? "",
        r.q1 ?? "",
        r.q2 ?? "",
        r.postionFrom ?? "",
        r.positionTo ?? "",
        r.text ?? "",
      ]
        .map(csvEscape)
        .join(",")
    );
  });
  return lines.join("\r\n");
}

export function downloadVerifiedCsv(rows: VerifiedRow[], filename = "verified-detections.csv"): void {
  const csv = toVerifiedCsv(rows);
  // BOM so Excel reads UTF-8 correctly.
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  triggerDownload(blob, filename);
}

export interface MainInfoPair {
  key: string;
  value: string;
}

const htmlEscape = (v: unknown): string =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

// A self-contained analysis report (inline styles, frame images inlined by URL)
// that opens in any browser. Reflects the verified/edited result — separate from
// the raw OCR exports.
export interface VerifiedHtmlOptions {
  title?: string;
  // Optional captured Video Analysis diagram (PNG data URL) to embed.
  graphImage?: string;
}

export function toVerifiedHtml(
  rows: VerifiedRow[],
  mainInfo: MainInfoPair[] = [],
  opts: VerifiedHtmlOptions = {}
): string {
  const title = opts.title ?? "Inspection Report";
  const mainInfoRows = mainInfo
    .filter((m) => (m.key ?? "").trim() !== "")
    .map(
      (m) =>
        `<tr><th scope="row">${htmlEscape(m.key)}</th><td>${htmlEscape(m.value)}</td></tr>`
    )
    .join("");

  const detectionRows = rows
    .map((r) => {
      const frame = (r as VerifiedRow & { frameUrl?: string }).frameUrl;
      const frameCell = frame
        ? `<img src="${htmlEscape(frame)}" alt="frame" style="max-width:120px;max-height:80px;border:1px solid #ccc;border-radius:4px;" />`
        : "";
      return `<tr>
        <td>${htmlEscape(timeLabel(r.timestamp))}</td>
        <td>${Number(r.distance ?? 0).toFixed(2)}</td>
        <td>${htmlEscape(r.main_Code)}</td>
        <td>${htmlEscape(r.c1)}</td>
        <td>${htmlEscape(r.c2)}</td>
        <td>${htmlEscape(r.sa)}</td>
        <td>${htmlEscape(r.q1)}</td>
        <td>${htmlEscape(r.q2)}</td>
        <td>${htmlEscape(r.postionFrom)}</td>
        <td>${htmlEscape(r.positionTo)}</td>
        <td>${htmlEscape(r.text)}</td>
        <td>${frameCell}</td>
      </tr>`;
    })
    .join("");

  const headerCells = [...HEADERS, "Frame"].map((h) => `<th>${htmlEscape(h)}</th>`).join("");

  const graphSection = opts.graphImage
    ? `<h2>Damage Detection</h2><img class="graph" src="${htmlEscape(opts.graphImage)}" alt="Damage detection graph" />`
    : "";

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>${htmlEscape(title)}</title>
<style>
  body { font-family: Arial, Helvetica, sans-serif; color: #1b2a38; margin: 24px; }
  h1 { font-size: 20px; }
  h2 { font-size: 15px; margin-top: 28px; }
  table { border-collapse: collapse; width: 100%; margin-top: 8px; font-size: 13px; }
  th, td { border: 1px solid #d0d7de; padding: 6px 8px; text-align: left; vertical-align: top; }
  thead th { background: #f3f6f9; }
  .main-info th { width: 220px; background: #f3f6f9; }
  img.graph { max-width: 100%; height: auto; border: 1px solid #d0d7de; border-radius: 4px; margin-top: 8px; }
</style>
</head>
<body>
  <h1>${htmlEscape(title)}</h1>
  ${mainInfoRows ? `<h2>Main Information</h2><table class="main-info"><tbody>${mainInfoRows}</tbody></table>` : ""}
  ${graphSection}
  <h2>Detections</h2>
  <table><thead><tr>${headerCells}</tr></thead><tbody>${detectionRows}</tbody></table>
</body>
</html>`;
}

export function downloadVerifiedHtml(
  rows: VerifiedRow[],
  mainInfo: MainInfoPair[] = [],
  opts: VerifiedHtmlOptions & { filename?: string } = {}
): void {
  const { filename = "inspection-report.html", ...htmlOpts } = opts;
  const html = toVerifiedHtml(rows, mainInfo, htmlOpts);
  const blob = new Blob([html], { type: "text/html;charset=utf-8;" });
  triggerDownload(blob, filename);
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
